import { assign, cloneDeep, flatMap, inRange, pick, union } from 'lodash';

import { PIVOT_QUADRANTS, QuadrantDefinition } from '../../../../common/constants';
import { LIST_TYPE_SINGLE, LIST_TYPE_SORTABLE } from '../draggable-item-list/draggable-item-list-controller';
import { ITEM_TYPE_DROPDOWN, ITEM_TYPE_SIMPLE } from '../draggable-item/draggable-item-controller';

type UpdateActionAddType = 'add';
type UpdateActionSetType = 'set';
type UpdateActionRemoveType = 'remove';
type UpdateAction = UpdateActionAddType | UpdateActionSetType | UpdateActionRemoveType;

const QUADRANTS_SELECTOR = '.pivot-config-quadrants';

const UPDATE_ACTION_ADD: UpdateActionAddType = 'add';
const UPDATE_ACTION_REMOVE: UpdateActionRemoveType = 'remove';
const UPDATE_ACTION_SET: UpdateActionSetType = 'set';

export default class PivotConfigController {
  static $inject = ['$element', '$timeout', '$sce'];

  height: number;
  columns: Array<any>;
  columnNames: Array<string>;
  introHtml = '';
  hideIntro = false;
  quadrants = PIVOT_QUADRANTS;
  quadrantMap: any;
  quadrantEls: Array<Element>;
  selectedListItem: { [$listId: number]: string } = {};
  onFilterChange: (args: { $field: string; $old: any }) => void;
  onValueChange: (args: { $field: string; $old: any }) => void;
  onColumnChange: (args: { $field: string; $old: any }) => void;
  onRowChange: (args: { $field: string; $old: any }) => void;
  onItemContextTriggered: (args: { $quadrant: string; $itemId: string }) => void;
  draggableStatus = { listId: '', itemId: '', dropped: true };

  dragInProgress = false;

  constructor(
    private $element: angular.IRootElementService,
    private $timeout: angular.ITimeoutService,
    private $sce: angular.ISCEService
  ) {}

  $onChanges = (changed) => {
    if (changed.columns) {
      this.initializeColumnNames();
    }
  };

  $postLink = () => {
    this.quadrantEls = this.$element.find('.pivot-config-quadrants .drag-drop-target').toArray();
  };

  /**
   * Initialize Column Names
   *
   * We cache column names for the component ui instead of retrieving
   * dynamically to prevent `10 $digest() iterations reach` error
   */
  initializeColumnNames() {
    this.columnNames = this.columns.map((c) => c.name);
  }

  /**
   * Handle Quadrant Update
   *
   * Mutate quadrant map property and delegate the
   * update to individual quadrant update handlers.
   *
   * @todo consider moving resolve action to a seperate method
   * @param $listId
   * @param $itemId
   * @param $action
   */
  handleQuadrantUpdate($listId: string, $itemId: string, $action: UpdateAction = UPDATE_ACTION_ADD) {
    const oldQuadrantMap = cloneDeep(this.quadrantMap);
    const resolveAction = (quadrant, action) => {
      const actionMap = {
        [`${UPDATE_ACTION_ADD}`]: {
          filters: UPDATE_ACTION_ADD,
          values: UPDATE_ACTION_SET,
          rows: UPDATE_ACTION_ADD,
          columns: UPDATE_ACTION_ADD,
        },
        [`${UPDATE_ACTION_REMOVE}`]: {
          filters: UPDATE_ACTION_REMOVE,
          values: UPDATE_ACTION_REMOVE,
          rows: UPDATE_ACTION_REMOVE,
          columns: UPDATE_ACTION_REMOVE,
        },
      };
      return actionMap[action][quadrant] || action;
    };
    const typeMap = {
      filters: ($field, $old) => {
        this.onFilterChange({ $field, $old });
      },
      values: ($field, $old) => {
        this.onValueChange({ $field, $old });
      },
      columns: ($field, $old) => {
        this.onColumnChange({ $field, $old });
      },
      rows: ($field, $old) => {
        this.onRowChange({ $field, $old });
      },
    };
    const action = resolveAction($listId, $action);

    if ($action !== UPDATE_ACTION_REMOVE) {
      assign(this.draggableStatus, {
        listId: $listId,
        dropped: true,
      });
    }

    typeMap[$listId](this.applyUpdateAction($listId, $itemId, action), oldQuadrantMap);
  }

  handleFieldListDragStart() {
    this.$timeout(() => (this.dragInProgress = true));
  }

  handleFieldListDragEnd() {
    this.$timeout(() => (this.dragInProgress = false));
  }

  /**
   * Apply Update Action
   *
   * @todo consider renaming this method
   * @param quadrant
   * @param value
   * @param actionType
   * @returns {any}
   */
  applyUpdateAction(quadrant, value, actionType) {
    const actionMap = {
      [`${UPDATE_ACTION_SET}`]: (property, value) => [value],
      [`${UPDATE_ACTION_ADD}`]: (property, value) => union(this.quadrantMap[property].slice(), [value]),
      [`${UPDATE_ACTION_REMOVE}`]: (property, value) => this.quadrantMap[property].slice().filter((v) => v !== value),
    };
    return (this.quadrantMap[quadrant] = actionMap[actionType](quadrant, value));
  }

  getFieldListHeight() {
    return this.height - this.$element.find(QUADRANTS_SELECTOR).outerHeight();
  }

  getIntroHtml() {
    return this.$sce.trustAsHtml(this.introHtml);
  }

  deleteQuadrantItem($contextId: string, $value: string) {
    this.handleQuadrantUpdate($contextId, $value, UPDATE_ACTION_REMOVE);
  }

  handleItemDragEnd($evt: DragEvent, $listId: string, $itemId: string) {
    const { listId, itemId, dropped } = this.draggableStatus;
    const droppedOutsideOfOrigin = (!dropped || listId !== $listId) && itemId === $itemId;

    if (droppedOutsideOfOrigin) {
      this.deleteQuadrantItem($listId, $itemId);
    }
  }

  handleItemDragStart($evt: DragEvent, $listId: string, $itemId: string) {
    assign(this.draggableStatus, {
      listId: $listId,
      itemId: $itemId,
      dropped: false,
    });
  }

  handleListItemSelection($listId: number, $itemId: string) {
    this.$timeout(() => {
      this.selectedListItem = {
        [`${$listId}`]: $itemId,
      };
    });
  }

  handleListItemTrigger($quadrant: string, $listId: number, $itemId: string) {
    this.onItemContextTriggered({ $quadrant, $itemId });
  }

  quadrantValues(quadrant: string) {
    const valuesMap = {
      filters: this.quadrantMap['filters'],
      rows: this.quadrantMap['rows'],
      columns: this.quadrantMap['columns'],
      values: this.quadrantMap['values'],
    };
    return valuesMap[quadrant];
  }

  quadrantHasValue(quadrant: string | Array<QuadrantDefinition>) {
    if (Array.isArray(quadrant)) {
      return !!flatMap(
        pick(
          this.quadrantMap,
          quadrant.map((q) => q.id)
        )
      ).length;
    } else {
      return !!(this.quadrantValues(quadrant) || []).length;
    }
  }

  getQuadrantDraggableType(quadrant: string) {
    const draggableTypeMap = {
      filters: ITEM_TYPE_DROPDOWN,
      rows: ITEM_TYPE_SIMPLE,
      columns: ITEM_TYPE_SIMPLE,
      values: ITEM_TYPE_DROPDOWN,
    };
    return draggableTypeMap[quadrant];
  }

  getQuadrantListType(quadrant: string) {
    const listTypeMap = {
      filters: LIST_TYPE_SORTABLE,
      rows: LIST_TYPE_SORTABLE,
      columns: LIST_TYPE_SORTABLE,
      values: LIST_TYPE_SINGLE,
    };
    return listTypeMap[quadrant];
  }

  /**
   * @todo move abbreviation maps to constants file
   * @param quadrant
   * @returns {any}
   */
  getAdditionalText(quadrant: string) {
    // aggregator abbreviations
    const aggrAbbrevMap = {
      DEFAULT: '?',
      sum: 'sum',
      average: 'avg',
      min: 'min',
      max: 'max',
      count: 'cnt',
      countDistinct: 'cnt d',
    };
    const additionalTextMap = {
      filters: '',
      rows: '',
      columns: '',
      values: aggrAbbrevMap[this.quadrantMap['aggregator']] || aggrAbbrevMap['DEFAULT'],
    };

    return additionalTextMap[quadrant];
  }
}
